/* eslint-disable */
import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Chip,
  Tooltip,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import SearchIcon from '@material-ui/icons/Search';
import VerifiedUserIcon from '@material-ui/icons/VerifiedUser';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import CheckIcon from '@material-ui/icons/Check';
import RefreshIcon from '@material-ui/icons/Refresh';
import PersonAddIcon from '@material-ui/icons/PersonAdd';
import { useHistory } from 'react-router';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import i18n from 'i18n-js';
import moment from 'moment';
import { baseScreen } from 'hoc';
import { useStores } from 'hooks';
import { EmsDoctorDto } from 'src/stores/backend/EmsStore/types';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  headerCard: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    borderRadius: 8,
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  titleIcon: {
    fontSize: 32,
    color: theme.palette.primary.main,
    marginRight: theme.spacing(1.5),
  },
  titleText: {
    fontWeight: 600,
    fontSize: '1.5rem',
  },
  subtitleText: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
  },
  searchContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      alignItems: 'stretch',
    },
  },
  searchInput: {
    flex: 1,
    backgroundColor: '#fff',
  },
  searchButton: {
    height: 56,
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
    fontWeight: 600,
  },
  tableCard: {
    borderRadius: 8,
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  },
  tableHeader: {
    backgroundColor: theme.palette.primary.main,
    '& th': {
      color: '#fff',
      fontWeight: 600,
      fontSize: '0.95rem',
    },
  },
  tableRow: {
    '&:nth-of-type(even)': {
      backgroundColor: '#f9fafb',
    },
    '&:hover': {
      backgroundColor: '#f0f4ff',
    },
  },
  syndicateChip: {
    fontWeight: 600,
    backgroundColor: '#e8f4fd',
    color: '#0288d1',
  },
  consultantChip: {
    fontWeight: 600,
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
  },
  errorBox: {
    padding: theme.spacing(2),
    backgroundColor: '#ffebee',
    border: '1px solid #ffcdd2',
    borderRadius: 6,
    marginBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  emptyBox: {
    textAlign: 'center',
    padding: theme.spacing(6),
    color: theme.palette.text.secondary,
  },
  actionsCell: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
}));

const EmsInquiryComponent: React.FC = () => {
  const classes = useStyles();
  const history = useHistory();
  const {
    backend: { ems },
  } = useStores();

  const [searchQuery, setSearchQuery] = useState('');
  const [copiedId, setCopiedId] = useState<number | null>(null);

  const handleSearch = async (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }
    const trimmed = searchQuery.trim();
    if (trimmed) {
      await ems.searchDoctors(trimmed);
    }
  };

  const handleCopy = (id: number) => {
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const formatYear = (dateStr: string) => {
    if (!dateStr) return '-';
    const parsed = moment(dateStr);
    return parsed.isValid() ? parsed.format('YYYY-MM-DD') : dateStr;
  };

  return (
    <div className={classes.root}>
      {/* Header & Search Section */}
      <Card className={classes.headerCard}>
        <CardContent>
          <div className={classes.titleContainer}>
            <VerifiedUserIcon className={classes.titleIcon} />
            <Typography variant="h5" className={classes.titleText}>
              {i18n.t('EMS_INQUIRY')}
            </Typography>
          </div>
          <Typography variant="body2" className={classes.subtitleText}>
            {i18n.t('EMS_INQUIRY_DESCRIPTION')}
          </Typography>

          <form onSubmit={handleSearch}>
            <div className={classes.searchContainer}>
              <TextField
                variant="outlined"
                fullWidth
                className={classes.searchInput}
                placeholder={i18n.t('EMS_SEARCH_PLACEHOLDER')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                variant="contained"
                color="primary"
                className={classes.searchButton}
                onClick={() => handleSearch()}
                disabled={ems.isLoading || !searchQuery.trim()}
                startIcon={
                  ems.isLoading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <SearchIcon />
                  )
                }
              >
                {i18n.t('SEARCH')}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Error Display */}
      {ems.error && (
        <Box className={classes.errorBox}>
          <Typography color="error" variant="body1">
            {i18n.t(ems.error) || ems.error}
          </Typography>
          <Button
            size="small"
            color="secondary"
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => handleSearch()}
          >
            {i18n.t('RETRY') || 'Retry'}
          </Button>
        </Box>
      )}

      {/* Results Table Section */}
      <Card className={classes.tableCard}>
        <Box
          p={2}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h6" style={{ fontWeight: 600 }}>
            {i18n.t('SEARCH_EMS_DOCTORS')}
          </Typography>
          {ems.totalCount > 0 && (
            <Chip
              label={`${i18n.t('TOTAL') || 'Total'}: ${ems.totalCount}`}
              color="primary"
              variant="outlined"
              size="small"
            />
          )}
        </Box>

        <TableContainer component={Paper} elevation={0}>
          <Table>
            <TableHead className={classes.tableHeader}>
              <TableRow>
                <TableCell>{i18n.t('SYNDICATE_NUMBER')}</TableCell>
                <TableCell>{i18n.t('DOCTOR_NAME')}</TableCell>
                <TableCell>{i18n.t('SPECIALIZATION')}</TableCell>
                <TableCell>{i18n.t('CONSULTANT_TYPE')}</TableCell>
                <TableCell>{i18n.t('REGISTRATION_YEAR')}</TableCell>
                <TableCell align="center">{i18n.t('ACTIONS') || 'Actions'}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ems.isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" style={{ padding: 40 }}>
                    <CircularProgress size={40} />
                  </TableCell>
                </TableRow>
              ) : ems.doctorsList.length > 0 ? (
                ems.doctorsList.map((doc: EmsDoctorDto, index: number) => (
                  <TableRow key={`${doc.id}-${doc.specialization}-${index}`} className={classes.tableRow}>
                    <TableCell>
                      <Chip
                        label={doc.id}
                        size="small"
                        className={classes.syndicateChip}
                      />
                    </TableCell>
                    <TableCell style={{ fontWeight: 600 }}>
                      {doc.doctorName}
                    </TableCell>
                    <TableCell>{doc.specialization}</TableCell>
                    <TableCell>
                      <Chip
                        label={doc.consultantType}
                        size="small"
                        className={classes.consultantChip}
                      />
                    </TableCell>
                    <TableCell>{formatYear(doc.registrationYear)}</TableCell>
                    <TableCell align="center">
                      <div className={classes.actionsCell}>
                        <CopyToClipboard
                          text={`${doc.doctorName} - ${doc.id} - ${doc.specialization}`}
                          onCopy={() => handleCopy(doc.id)}
                        >
                          <Tooltip title={copiedId === doc.id ? i18n.t('COPIED') : i18n.t('CLICK_TO_COPY') || 'Copy'}>
                            <IconButton size="small" color="primary">
                              {copiedId === doc.id ? (
                                <CheckIcon style={{ color: 'green' }} />
                              ) : (
                                <FileCopyIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                        </CopyToClipboard>
                        <Tooltip title={i18n.t('CREATE_DOCTOR_FROM_EMS')}>
                          <Button
                            variant="outlined"
                            color="primary"
                            size="small"
                            startIcon={<PersonAddIcon fontSize="small" />}
                            onClick={() => {
                              history.push('/doctor-details/new/:id');
                            }}
                          >
                            {i18n.t('ADD_NEW_DOCTOR')}
                          </Button>
                        </Tooltip>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6}>
                    <div className={classes.emptyBox}>
                      <Typography variant="body1">
                        {searchQuery ? i18n.t('NO_EMS_RESULTS') : i18n.t('EMS_SEARCH_PLACEHOLDER')}
                      </Typography>
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </div>
  );
};

export const EmsInquiry = baseScreen(EmsInquiryComponent, {
  allowedRoles: ['NONE', 'ADMIN', 'SUPER_ADMIN'],
});
